import { defineAction } from '~/prix'
import { z } from 'zod'
import revalidateLocationAction from './legalEntityRevalidateLocation.action.json' with { type: 'json' }

export const revalidateLocationStrategyZod = z.enum([
  'attendanceHistoryCluster',
  'placesTextSearchPro',
  'googleGeocoding',
])

export const revalidateLocationSourceZod = z.enum([
  'auto',
  'attendanceHistoryCluster',
  'placesTextSearchPro',
  'googleGeocoding',
])

export const revalidateLocationCacheEventStatusZod = z.enum([
  'hit',
  'miss',
  'waitHit',
  'waitMiss',
  'locked',
  'write',
  'writeSkipped',
])

export const revalidateLocationConfidenceLabelZod = z.enum(['high', 'medium', 'low'])

export const revalidateLocationLatLngZod = z.object({
  latitude: z.number(),
  longitude: z.number(),
})

export const revalidateLocationBoundsZod = z.object({
  northeast: revalidateLocationLatLngZod,
  southwest: revalidateLocationLatLngZod,
})

export const revalidateLocationAddressComponentZod = z.object({
  longName: z.string(),
  shortName: z.string(),
  types: z.array(z.string()),
})

export const revalidateLocationAttributionZod = z.object({
  provider: z.string(),
  providerLabel: z.string().optional(),
  source: revalidateLocationStrategyZod.optional(),
  placeId: z.string().optional(),
  quality: z.string().optional(),
  mapsUri: z.string().optional(),
})

export const revalidateLocationInputZod = z.object({
  id: z.string().min(1).describe('ID da empresa a ser revalidada em provedores de localização.'),
  source: revalidateLocationSourceZod.optional(),
  strategyChain: z.array(revalidateLocationStrategyZod).min(1).optional(),
})

export const revalidateLocationQueryZod = z.object({
  source: revalidateLocationStrategyZod,
  key: z.string(),
  searchText: z.string(),
  searchParts: z.array(z.string()),
  status: z.string(),
  resultCount: z.number(),
  componentsFilter: z.array(z.string()).optional(),
  errorMessage: z.string().optional(),
  confidenceThresholdMet: z.boolean().optional(),
})

export const revalidateLocationResultZod = z.object({
  placeId: z.string(),
  source: revalidateLocationStrategyZod,
  matchedBySources: z.array(revalidateLocationStrategyZod),
  matchedByQueries: z.array(z.string()),
  displayName: z.string().optional(),
  formattedAddress: z.string(),
  partialMatch: z.boolean().optional(),
  locationType: z.string().optional(),
  latitude: z.number(),
  longitude: z.number(),
  types: z.array(z.string()),
  primaryType: z.string().optional(),
  postcodeLocalities: z.array(z.string()).optional(),
  businessStatus: z.string().optional(),
  googleMapsUri: z.string().optional(),
  addressComponents: z.array(revalidateLocationAddressComponentZod),
  viewport: revalidateLocationBoundsZod.optional(),
  bounds: revalidateLocationBoundsZod.optional(),
  confidenceScore: z.number().optional(),
  confidenceLabel: revalidateLocationConfidenceLabelZod.optional(),
  accuracyMeters: z.number().optional(),
  supportCount: z.number().int().positive().optional(),
})

export const revalidateLocationPlacesResultZod = z.object({
  placeId: z.string(),
  source: revalidateLocationStrategyZod,
  matchedByQueries: z.array(z.string()),
  displayName: z.string().optional(),
  formattedAddress: z.string().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  types: z.array(z.string()),
  primaryType: z.string().optional(),
  businessStatus: z.string().optional(),
  googleMapsUri: z.string().optional(),
  addressComponents: z.array(revalidateLocationAddressComponentZod),
  viewport: revalidateLocationBoundsZod.optional(),
  confidenceScore: z.number().optional(),
  confidenceLabel: revalidateLocationConfidenceLabelZod.optional(),
})

export const revalidateLocationCacheEventZod = z.object({
  source: revalidateLocationStrategyZod,
  status: revalidateLocationCacheEventStatusZod,
  resultCount: z.number().optional(),
  placesResultCount: z.number().optional(),
  succeededSource: revalidateLocationStrategyZod.optional(),
  cacheable: z.boolean().optional(),
  externalRequest: z.boolean().optional(),
})

export const revalidateLocationOutputZod = z.object({
  legalEntity: z.object({
    id: z.string(),
    tradeName: z.string().optional(),
    corporateName: z.string().optional(),
    sanitizedName: z.string().optional(),
    placesSearchName: z.string().optional(),
    publicPlace: z.string().optional(),
    number: z.string().optional(),
    streetLine: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    postalCode: z.string().optional(),
    missingParts: z.array(z.string()),
    currentLocation: z
      .object({
        latitude: z.number().optional(),
        longitude: z.number().optional(),
        attribution: revalidateLocationAttributionZod,
      })
      .optional(),
  }),
  sourceSelection: z.object({
    requestedSource: revalidateLocationSourceZod,
    requestedStrategyChain: z.array(revalidateLocationStrategyZod),
    selectedPrimarySource: revalidateLocationStrategyZod,
    executedSources: z.array(revalidateLocationStrategyZod),
    succeededSource: revalidateLocationStrategyZod.optional(),
    fellBack: z.boolean(),
    placesMonthlyUsageCount: z.number().optional(),
    placesMonthlyUsageLimit: z.number().optional(),
    placesMonthlyUsageMonth: z.string().optional(),
    usedPlacesMonthlyQuota: z.boolean(),
    cacheEvents: z.array(revalidateLocationCacheEventZod),
  }),
  queries: z.array(revalidateLocationQueryZod),
  results: z.array(revalidateLocationResultZod),
  placesResults: z.array(revalidateLocationPlacesResultZod),
})

export type RevalidateLocationInput = z.infer<typeof revalidateLocationInputZod>
export type RevalidateLocationSource = z.infer<typeof revalidateLocationSourceZod>
export type RevalidateLocationStrategy = z.infer<typeof revalidateLocationStrategyZod>
export type RevalidateLocationCacheEvent = z.infer<typeof revalidateLocationCacheEventZod>
export type RevalidateLocationOutput = z.infer<typeof revalidateLocationOutputZod>

const revalidateLocationDefinition = defineAction({
  ...revalidateLocationAction,
  input: revalidateLocationInputZod as any,
  output: revalidateLocationOutputZod as any,
})

export default revalidateLocationDefinition
